import { isTruthy } from '@ardoq/common-helpers';
import {
  APIComponentAttributes,
  APIFieldType,
  ArdoqId,
} from '@ardoq/api-types';
import { toIds } from '@ardoq/core';
import { uniq } from 'lodash';
import {
  RelevantCompontsScopeDataMap,
  TimelineViewInterface,
  ViewRelevantWorkspaceScopeData,
  WorkspaceAndFieldsRegistry,
} from '../types';
import { normalizeToLocalDate, parseDate } from '@ardoq/date-time';
import { colors } from '@ardoq/design-tokens';

type GetViewRelevantComponentsScopeDataArgs = {
  allComponentIds: string[];
  workspaceAndFieldsRegistry: WorkspaceAndFieldsRegistry;
  viewInterface: TimelineViewInterface;
  childrenByParent: Map<ArdoqId, APIComponentAttributes[]>;
};

/**
 * Contains all component data that the view should need to display the components and fields
 */
const getViewRelevantComponentsScopeData = ({
  viewInterface,
  workspaceAndFieldsRegistry,
  allComponentIds,
  childrenByParent,
}: GetViewRelevantComponentsScopeDataArgs): RelevantCompontsScopeDataMap => {
  const {
    get: getComponent,
    getRepresentationData,
    getComponentTypeId,
    isComponentIncludedInContextByFilter,
    getComponentDisplayColor,
    getComponentFieldValue,
    getComponentWorkspaceId,
  } = viewInterface;

  return new Map(
    allComponentIds.map(id => {
      const children = toIds(childrenByParent.get(id) ?? []);

      const componentWorkspaceId = getComponentWorkspaceId(id)!;
      const componentTypeId = getComponentTypeId(id);

      const allDateFieldsForComponentType = componentTypeId
        ? (workspaceAndFieldsRegistry
            .get(componentWorkspaceId)
            ?.byComponentType.get(componentTypeId) ?? [])
        : [];

      const componentDateFieldValues = allDateFieldsForComponentType.reduce<
        Record<string, Date | null>
      >((acc, { name }) => {
        const fieldValue = getComponentFieldValue(id, name);
        const formattedValue =
          typeof fieldValue === 'string'
            ? parseDate(normalizeToLocalDate(fieldValue))
            : null;

        acc[name] = formattedValue;

        return acc;
      }, {});

      return [
        id,
        {
          id,
          workspaceId: componentWorkspaceId,
          representationData: getRepresentationData(id),
          componentTypeId,
          children,
          hasChildren: Boolean(children?.length),
          isIncludedInContextByFilter: (() => {
            const component = getComponent(id);
            return component
              ? isComponentIncludedInContextByFilter(component)
              : true;
          })(),
          componentColor:
            getComponentDisplayColor(id, {
              useAsBackgroundStyle: false,
            })?.fill || colors.black950,
          dateFields: allDateFieldsForComponentType,
          dateFieldValues: componentDateFieldValues,
        },
      ];
    })
  );
};

type GetViewRelevantWorkspacesScopeDataArgs = {
  allWorkspaceIds: string[];
  viewRelevantComponentsScopeData: RelevantCompontsScopeDataMap;
  workspaceAndFieldsRegistry: WorkspaceAndFieldsRegistry;
  viewInterface: TimelineViewInterface;
};

/** *
 * This function calls model interface methods passed via state in viewInterface.
 * Since we cannot use scoped data directly yet, and to avoid code duplication, we have to
 * use this trickery until direct access to enhanced scope data is provided.
 */
const getViewRelevantWorkspacesScopeData = ({
  allWorkspaceIds,
  viewRelevantComponentsScopeData,
  viewInterface,
  workspaceAndFieldsRegistry,
}: GetViewRelevantWorkspacesScopeDataArgs): ViewRelevantWorkspaceScopeData[] => {
  const { getWorkspaceNameById } = viewInterface;
  return allWorkspaceIds.map(workspaceId => {
    const workspaceAndFieldRegistryObj =
      workspaceAndFieldsRegistry.get(workspaceId);

    const componentsInWorkspace = [
      ...viewRelevantComponentsScopeData.values(),
    ].filter(
      ({ workspaceId: componentWorkspaceId }) =>
        componentWorkspaceId === workspaceId
    );

    const allComponentTypesInWorkspace =
      workspaceAndFieldRegistryObj?.allComponentTypesInWorkspace ?? [];

    const componentTypeIdsOfComponentsInScope =
      allComponentTypesInWorkspace.filter(componentType =>
        componentsInWorkspace.some(
          ({ componentTypeId }) => componentTypeId === componentType.id
        )
      );

    // all date time fields in the workspace, that apply to available components
    const relevantFieldsInWorkspace =
      workspaceAndFieldRegistryObj?.allDateFields?.filter(
        ({ componentType: fieldComponentTypes, global }) =>
          global ||
          componentTypeIdsOfComponentsInScope.some(
            ({ id: componentInWorkspaceTypeId }) =>
              fieldComponentTypes.includes(componentInWorkspaceTypeId)
          )
      ) ?? [];

    return {
      id: workspaceId,
      name: getWorkspaceNameById(workspaceId),
      componentTypes: componentTypeIdsOfComponentsInScope,
      relevantFieldsInWorkspace,
      fieldsByComponentType:
        workspaceAndFieldRegistryObj?.byComponentType ?? new Map(),
    };
  });
};

type GetViewRelevantScopeData = (args: {
  allComponentIds: string[];
  viewInterface: TimelineViewInterface;
  childrenByParent: Map<ArdoqId, APIComponentAttributes[]>;
}) => {
  viewRelevantComponentsScopeData: RelevantCompontsScopeDataMap;
  viewRelevantWorkspacesScopeData: ViewRelevantWorkspaceScopeData[];
  workspaceAndFieldsRegistry: WorkspaceAndFieldsRegistry;
};
export const getViewRelevantScopeData: GetViewRelevantScopeData = ({
  allComponentIds,
  viewInterface,
  childrenByParent,
}) => {
  const {
    getComponentWorkspaceId,
    getComponentTypesForWorkspace,
    getFieldsOfWorkspace,
    getWorkspaceNameById,
  } = viewInterface;

  const allWorkspaceIds = uniq(
    allComponentIds.map(getComponentWorkspaceId)
  ).filter(isTruthy);

  /**
   * All date time fields in the workspace, grouped by workspace model id and component type within the workspace
   */
  const workspaceAndFieldsRegistry: WorkspaceAndFieldsRegistry = new Map(
    allWorkspaceIds.map(workspaceId => {
      const allDateFieldsInWorkspace = getFieldsOfWorkspace(workspaceId, [
        APIFieldType.DATE_ONLY,
        APIFieldType.DATE_TIME,
      ]);

      const allComponentTypesInWorkspace =
        getComponentTypesForWorkspace(workspaceId);

      return [
        workspaceId,
        {
          name: getWorkspaceNameById(workspaceId),
          allDateFields: allDateFieldsInWorkspace,
          allComponentTypesInWorkspace,
          byComponentType: new Map(
            allComponentTypesInWorkspace.map(({ id: componentTypeId }) => {
              return [
                componentTypeId,
                allDateFieldsInWorkspace.filter(
                  ({ componentType, global }) =>
                    global || componentType.includes(componentTypeId)
                ),
              ];
            })
          ),
        },
      ];
    })
  );

  const viewRelevantComponentsScopeData: RelevantCompontsScopeDataMap =
    getViewRelevantComponentsScopeData({
      viewInterface,
      workspaceAndFieldsRegistry,
      allComponentIds,
      childrenByParent,
    });

  const viewRelevantWorkspacesScopeData: ViewRelevantWorkspaceScopeData[] =
    getViewRelevantWorkspacesScopeData({
      allWorkspaceIds,
      viewRelevantComponentsScopeData,
      viewInterface,
      workspaceAndFieldsRegistry,
    });

  return {
    workspaceAndFieldsRegistry,
    viewRelevantComponentsScopeData,
    viewRelevantWorkspacesScopeData,
  };
};
